import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, ChevronDown, Languages } from 'lucide-react';
import { getLanguages } from '../redux/translationSlice';
import { translateAppointmentSummary, getAppointmentSummary } from '../redux/appointmentSlice';
import { getSocket } from '../socket';

// Clinical AI summary for the appointment — generated on completion in the
// doctor's language. A language dropdown (from master → Translation → Language)
// lets the viewer switch language; the summary is AI-translated (and cached)
// on demand, with a "Generating…" state. Shown only for completed appointments.
export default function AppointmentSummary({ appointment, className = '' }) {
    const { t } = useTranslation(['appointmentdetails']);
    const dispatch = useDispatch();
    const isCompleted = /completed/i.test(appointment?.status || '');
    const languages = useSelector((s) => s.translation?.languages || []);

    // While the background summary is being generated we poll for it. `fetched`
    // holds the summary once it lands; `pendingGen` means inputs (transcript /
    // STT / OCR) are still generating.
    const [fetched, setFetched] = useState(null);
    const [pendingGen, setPendingGen] = useState(false);

    // Stored as JSON: { lang, text, translations: { <code>: text } }. Falls back
    // to plain text for any legacy value.
    const parsed = useMemo(() => {
        const raw = appointment?.aiAppointmentSummary || fetched;
        if (!raw) return null;
        try {
            const o = JSON.parse(raw);
            if (o && typeof o === 'object' && (o.text || o.translations)) {
                return { lang: o.lang || 'en', text: o.text || '', translations: o.translations || {} };
            }
        } catch (e) { /* legacy plain text */ }
        return { lang: 'en', text: String(raw), translations: {} };
    }, [appointment?.aiAppointmentSummary, fetched]);

    const [selectedLang, setSelectedLang] = useState(parsed?.lang || 'en');
    const [cache, setCache] = useState(parsed?.translations || {});
    const [loading, setLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Reset to the source language when the appointment changes.
    useEffect(() => {
        setSelectedLang(parsed?.lang || 'en');
        setCache(parsed?.translations || {});
        setMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointment?.appointmentId]);

    useEffect(() => {
        if (isCompleted && languages.length === 0) {
            dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
        }
    }, [isCompleted, languages.length, dispatch]);

    // Live push: the backend emits summary:ready the moment the summary is
    // stored → flip the loader to the summary instantly (no poll wait). The
    // socket may not be connected yet at mount, so retry attaching.
    useEffect(() => {
        const id = appointment?.appointmentId;
        if (!id) return undefined;
        let alive = true;
        let retryTimer = null;
        let attachedSocket = null;
        const onReady = (p) => { if (p && p.appointmentId === id && p.summary) setFetched(p.summary); };
        const attach = () => {
            if (!alive) return;
            const socket = getSocket && getSocket();
            if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
            attachedSocket = socket;
            socket.on('summary:ready', onReady);
        };
        attach();
        return () => {
            alive = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (attachedSocket) attachedSocket.off('summary:ready', onReady);
        };
    }, [appointment?.appointmentId]);

    // Poll for the background-generated summary until it lands — BOUNDED so a
    // permanently-failed generation can't hammer the API forever (~10 min max;
    // the backend self-heals + throttles retries on its side).
    const [stalled, setStalled] = useState(false);
    useEffect(() => {
        const id = appointment?.appointmentId;
        if (!isCompleted || !id) return undefined;
        if (appointment?.aiAppointmentSummary || fetched) return undefined; // already have it
        let alive = true;
        let timer = null;
        let attempts = 0;
        const MAX_ATTEMPTS = 100;
        const tick = () => {
            attempts += 1;
            dispatch(getAppointmentSummary(id)).unwrap()
                .then((res) => {
                    if (!alive || !res) return;
                    setPendingGen(!!res.pending);
                    if (res.summary) { setFetched(res.summary); return; }
                    if (attempts >= MAX_ATTEMPTS) { setStalled(true); return; }
                    timer = setTimeout(tick, 6000);
                })
                .catch(() => {
                    if (!alive) return;
                    if (attempts >= MAX_ATTEMPTS) { setStalled(true); return; }
                    timer = setTimeout(tick, 8000);
                });
        };
        tick();
        return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [isCompleted, appointment?.appointmentId, appointment?.aiAppointmentSummary, fetched, dispatch]);

    useEffect(() => {
        const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        if (menuOpen) { document.addEventListener('mousedown', onClick); return () => document.removeEventListener('mousedown', onClick); }
    }, [menuOpen]);

    if (!isCompleted) return null;

    const hasSummary = !!parsed?.text;
    const currentText = cache[selectedLang] || (selectedLang === parsed?.lang ? parsed?.text : '');
    const langName = (code) => languages.find((l) => l.code === code)?.name || code;

    const pickLang = async (code, name) => {
        setMenuOpen(false);
        if (!code || code === selectedLang) return;
        setSelectedLang(code);
        if (cache[code] || code === parsed?.lang) return; // cached or source — no re-translate
        setLoading(true);
        try {
            const res = await dispatch(translateAppointmentSummary({
                appointmentId: appointment.appointmentId, lang: code, languageName: name,
            })).unwrap();
            if (res?.summary) setCache((c) => ({ ...c, [code]: res.summary }));
        } catch (e) { /* keep showing source on failure */ }
        finally { setLoading(false); }
    };

    return (
        <div data-testid="appointment-summary" className={`rounded-2xl border border-[#3AA8A8]/25 bg-gradient-to-br from-[#3AA8A8]/8 to-transparent p-4 sm:p-5 ${className}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#2D8A8A]" />
                    <h4 className="text-sm sm:text-base font-bold text-[#2D8A8A]">
                        {t('appointmentdetails.appointment_summary', 'Appointment summary')}
                    </h4>
                </div>

                {hasSummary && languages.length > 0 && (
                    <div className="relative" ref={menuRef}>
                        <button
                            type="button"
                            data-testid="appointment-summary-lang-button"
                            onClick={() => setMenuOpen((v) => !v)}
                            className="flex items-center gap-1 text-xs font-semibold text-[#3AA8A8] hover:text-[#2D8A8A] border border-[#4ABDBD]/30 rounded-lg px-2 py-1"
                        >
                            <Languages className="w-3.5 h-3.5" />
                            {langName(selectedLang)}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-1 z-30 w-40 max-h-56 overflow-y-auto custom-scrollbar bg-white rounded-xl shadow-lg border border-gray-200 py-1" data-testid="appointment-summary-lang-menu">
                                {languages.map((l) => (
                                    <button
                                        key={l.code || l.languageId}
                                        type="button"
                                        onClick={() => pickLang(l.code, l.name)}
                                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${l.code === selectedLang ? 'text-[#2D8A8A] font-semibold' : 'text-gray-700'}`}
                                    >
                                        {l.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!hasSummary ? (
                stalled ? (
                    <p data-testid="appointment-summary-stalled" className="text-sm text-gray-400 py-1">
                        {t('appointmentdetails.summary_delayed', 'The summary is taking longer than expected — it will appear here once ready.')}
                    </p>
                ) : (
                    <div data-testid="appointment-summary-generating" className="py-1">
                        <div className="flex items-center gap-2 text-sm text-amber-600">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {pendingGen
                                ? t('appointmentdetails.summary_waiting', 'Waiting for transcripts to finish…')
                                : t('appointmentdetails.generating_summary_full', 'Generating summary…')}
                        </div>
                        {/* Indeterminate progress bar (a true % isn't available from the LLM). */}
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#3AA8A8]/10">
                            <div className="h-full w-1/3 rounded-full bg-[#3AA8A8]/60 animate-pulse" />
                        </div>
                    </div>
                )
            ) : loading ? (
                <div data-testid="appointment-summary-generating" className="flex items-center gap-2 text-sm text-amber-600 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('appointmentdetails.generating_summary', 'Generating…')}
                </div>
            ) : (
                <p data-testid="appointment-summary-text" className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                    {currentText}
                </p>
            )}
        </div>
    );
}
